import React, { useMemo, useState } from 'react';
import { TechRotation } from '@michelin/core-profile-ui-tools';
import { Button, Container, useDialogs } from '@michelin/acid-components';
import { PageTitle } from 'Utils/CanvasPageTitle';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  useGetRotations, useGetRotationList, useSaveRotations, useSaveRotationList,
} from 'hooks/useTechRotationQueries';
import uuid from 'uuid';
import { useContactList, addContact } from 'hooks/useContactQueries';
import { useLocationShort } from 'hooks/useLocation';
import {
  ContactListItem, SPPContactTypeRoleAreasEnum, SPPContactTypeServicesEnum, SPPContactTypeLevelsEnum,
} from 'Types';
import AddRotationContactDialog from './AddRotationContactDialog';

const TechRotationPage = () => {
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();
  const permissions = usePermissions();
  const { stNumber } = useParams<{ stNumber: string }>();
  const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [addContactSaving, setAddContactSaving] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: rotations, isLoading: rotationsLoading } = useGetRotations(stNumber, date);
  const { data: rotationList, isLoading: rotationListLoading } = useGetRotationList(stNumber);
  const { data: contactsData, isLoading: contactsLoading } = useContactList({
    stNumber,
    accountLevel: '',
    contactType: SPPContactTypeRoleAreasEnum.rotation_technician,
    queryKey: undefined,
    limit: 1000,
  });
  const { data: locationData } = useLocationShort(stNumber);

  const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object') {
      const e = error as any;
      return e.error || e.message || e.exception || JSON.stringify(error);
    }
    return String(error);
  };

  const saveRotationsMutation = useSaveRotations(stNumber, (error) => errorDialog([t('Could not save rotation'), getErrorMessage(error)], t('Error')));
  const saveRotationListMutation = useSaveRotationList(stNumber, (error) => errorDialog([t('Could not save rotation list'), getErrorMessage(error)], t('Error')));

  const contacts = useMemo(() => {
    const list: ContactListItem[] = Array.isArray(contactsData)
      ? contactsData
      : ((contactsData as any)?.data ?? []);
    return list.map((c) => ({
      id: c.id,
      name: `${c.first_name} ${c.last_name}`,
      email: c.email_address || undefined,
      primaryPhoneNumber: c.cell_phone || undefined,
      secondaryPhoneNumber: c.work_phone || undefined,
    }));
  }, [contactsData]);

  const country = useMemo((): 'US' | 'CA' => {
    const c = locationData?.customer_country?.toUpperCase();
    return c === 'CA' ? 'CA' : 'US';
  }, [locationData]);

  const readOnly = !permissions.allowsAction('technician_rotation.update');

  const techRotationPermissions = {
    update_past_rotations: permissions.allowsAction('technician_rotation.update_past_rotations'),
    reset_rotations: permissions.allowsAction('technician_rotation.reset_rotations'),
  };

  const handleChange = (newRotations: any[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const cleaned = newRotations.map(({ id, ...rotation }: any) => {
      const result: Record<string, any> = {};
      Object.entries(rotation).forEach(([key, value]) => {
        if (value !== null && value !== undefined) result[key] = value;
      });
      if (result.from !== undefined) result.from = Number(result.from);
      if (result.to !== undefined) result.to = Number(result.to);
      return result;
    });
    saveRotationsMutation.mutate(cleaned);
  };

  const handleRotationListSave = (newRotationList: any) => {
    saveRotationListMutation.mutate(newRotationList);
  };

  const handleAddContact = async (data: { firstName: string; lastName: string; jobTitle: string; phoneNumber: string }) => {
    setAddContactSaving(true);
    try {
      const contactData = {
        contact_level: 'ST',
        first_name: data.firstName,
        last_name: data.lastName,
        job_title: data.jobTitle || undefined,
        cell_phone: data.phoneNumber,
        preferred_language: 'english',
        preferred_method: 'call',
        group: false,
        visible_externally: true,
        work_hours: {
          monday: { begin: '08:00', end: '17:00' },
          tuesday: { begin: '08:00', end: '17:00' },
          wednesday: { begin: '08:00', end: '17:00' },
          thursday: { begin: '08:00', end: '17:00' },
          friday: { begin: '08:00', end: '17:00' },
          saturday: { begin: undefined, end: undefined },
          sunday: { begin: undefined, end: undefined },
        },
        contact_types: [
          {
            id: uuid.v4(),
            service: SPPContactTypeServicesEnum.retail,
            role_areas: [{
              role_area: SPPContactTypeRoleAreasEnum.rotation_technician,
              levels: [{ level: SPPContactTypeLevelsEnum.general, location: `10~${stNumber}` }],
            }],
          },
          {
            id: uuid.v4(),
            service: SPPContactTypeServicesEnum.commercial,
            role_areas: [{
              role_area: SPPContactTypeRoleAreasEnum.rotation_technician,
              levels: [{ level: SPPContactTypeLevelsEnum.general, location: `10~${stNumber}` }],
            }],
          },
        ],
      };

      const response = await addContact(stNumber, contactData);

      if (response?.status === 1) {
        errorDialog([t('Could not add contact'), response.message || ''], t('Error'));
      } else {
        const newContact = {
          id: response.data?.id,
          contact_level: 'ST',
          first_name: data.firstName,
          last_name: data.lastName,
          email_address: '',
          cell_phone: data.phoneNumber,
          work_phone: '',
          ext: '',
          contact_types: contactData.contact_types,
          missing_info: false,
        };
        queryClient.setQueryData(
          ['contact-list', stNumber, '', SPPContactTypeRoleAreasEnum.rotation_technician, undefined, 1000],
          (old: any) => ({
            ...old,
            data: [...(old?.data ?? []), newContact],
            total: (old?.total ?? 0) + 1,
          }),
        );
        enqueueSnackbar(t('Contact added successfully.'), { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        setAddContactOpen(false);
      }
    } catch (error) {
      errorDialog([t('Could not add contact'), getErrorMessage(error)], t('Error'));
    } finally {
      setAddContactSaving(false);
    }
  };

  return (
    <>
      <PageTitle
        title={t('Tech Rotation')}
        goHomeBtn
        rightItems={!readOnly ? (
          <Button color="primary" onClick={() => setAddContactOpen(true)} style={{ whiteSpace: 'nowrap' }}>
            {t('Add Rotation Contact')}
          </Button>
        ) : undefined}
      />
      <Container>

        <TechRotation
          from="spp"
          date={date}
          rotations={rotations ?? null}
          contacts={contacts}
          onChange={handleChange}
          onDateChange={setDate}
          country={country}
          isLoading={rotationsLoading || rotationListLoading || contactsLoading}
          isSaving={saveRotationsMutation.isLoading || saveRotationListMutation.isLoading}
          rotationList={rotationList ?? null}
          onRotationListSave={handleRotationListSave}
          readOnly={readOnly}
          permissions={techRotationPermissions}
        />
      </Container>

      <AddRotationContactDialog
        open={addContactOpen}
        country={country}
        onClose={() => setAddContactOpen(false)}
        onSave={handleAddContact}
        isSaving={addContactSaving}
      />
    </>
  );
};

export default TechRotationPage;
